/* eslint-disable no-undef */
import { CircularProgress } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import Cookie from "js-cookie";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import NotAuthorized from "./Routes/notAuthorized";
import ar from "./Themes/ar";
import en from "./Themes/en";
import AppLayout from "./components/appLayout";
import UserStatusContext from "./context/UserStatus";
import { SocketContext, socket, connectUser } from "./context/Socket";
import VerifyToken from "./pages/VerifyToken";
import { USER_LOGIN_SUCCESS } from "./redux/users/constants";
import login from "./redux/users/thunks/login";
import logout from "./redux/users/thunks/logout";
import Theme from "./rtlConfiguration";
import { setIsSafari } from "./redux/browser/actions";
import { changeTheme } from "./redux/langauge/actions";

import "./App.css";

const App = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [needAuth, setNeedAuth] = useState(false);
  const { userInfo } = useSelector((state) => state.user);
  const userCookie = Cookie.getJSON("userInfo");
  const { language, themeMode } = useSelector((state) => state.language); // Get themeMode from Redux store
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState("");

  const [userData, setUserData] = useState();

  const checkIfUserLoggedIn = (data) => {
    if (data && data.two_factor_enabled === true) {
      setNeedAuth(true);
      setIsLoggedIn(true);
      setIsLoading(false);
      connectUser(data._id);
    } else if (userCookie && userCookie.token) {
      setNeedAuth(false);
      setIsLoggedIn(true);
      setIsLoading(false);
      connectUser(data._id);
    } else {
      setIsLoggedIn(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf("safari") !== -1 && ua.indexOf("chrome") === -1) {
      dispatch(setIsSafari(true));
    }
  }, [dispatch]);

  useEffect(() => {
    try {
      const checkVerification = async () => {
        try {
          const {
            data: { data },
          } = await axios.get(`/api/v1/verify-user`, {
            headers: {
              Authorization: userCookie?.token,
            },
          });
          setUserData({ ...data, isUserVerified: false, isLoggedIn: true });
          dispatch({ type: USER_LOGIN_SUCCESS, payload: { ...data, ...userCookie } });
        } catch (err) {
          Cookie.remove("userInfo");
          setIsLoggedIn(false);
          setIsLoading(false);
        }
      };
      if (userCookie?.token) {
        checkVerification();
        return;
      }
      setIsLoggedIn(false);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (userData && Object.keys(userData) && Object.keys(userData).length)
      checkIfUserLoggedIn(userData);
  }, [userData]);

  // Clear console
  // useEffect(() => {
  //   if (typeof console._commandLineAPI !== "undefined") {
  //     console.API = console._commandLineAPI;
  //   } else if (typeof console._inspectorCommandLineAPI !== "undefined") {
  //     console.API = console._inspectorCommandLineAPI;
  //   } else if (typeof console.clear !== "undefined") {
  //     console.API = console;
  //   }
  //   console.API.clear();
  // });

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // To translate content based on selected language
  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  // For editing the body direction based on the selected language
  useEffect(() => {
    document
      .getElementsByTagName("body")[0]
      .setAttribute("dir", i18n.language === "ar" ? "rtl" : "ltr");
    document.title = i18n.language === "ar" ? "راسلني" : "Rasilni";
  });

  const theme = themeMode === "dark" ? en.dark : en.light;

  useLayoutEffect(() => {
    document.body.style.backgroundColor = themeMode === "dark" ? "#212121" : "white";
  }, [themeMode]);

  return (
    <Theme>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          {/* <React.StrictMode> */}
          <BrowserRouter>
            <SocketContext.Provider value={socket}>
              <UserStatusContext.Provider
                value={{
                  isLoggedIn,
                  userData,
                  logout: () => {
                    setIsLoggedIn(false);
                    dispatch(logout(userInfo?.token));
                    socket.disconnect();
                  },
                  login: async (data) => {
                    setIsLoggedIn(true);
                    const loginData = await dispatch(login(data));
                    setUserData(loginData?.payload);
                    if (socket.connected) connectUser(loginData?.payload?._id);
                  },
                  updateUserInfo: (data) => {
                    setUserData(data);
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress />
                ) : userCookie && isLoggedIn && needAuth === false ? (
                  <AppLayout />
                ) : isLoggedIn &&
                  needAuth === true &&
                  userCookie &&
                  userCookie.isUserVerified === false ? (
                  <VerifyToken />
                ) : isLoggedIn &&
                  needAuth === true &&
                  userCookie &&
                  userCookie.isUserVerified === true ? (
                  <AppLayout />
                ) : (
                  <NotAuthorized />
                )}
              </UserStatusContext.Provider>
            </SocketContext.Provider>
          </BrowserRouter>
          {/* </React.StrictMode> */}
        </ThemeProvider>
      </StyledEngineProvider>
    </Theme>
  );
};

export default App;
